<template>
  <div class="image-viewer--wrapper">
    <IconButton
      icon="cross"
      form="transparent"
      class="close-btn"
      @click="closeViewer"
    />
    <div
      ref="viewer"
      class="image-viewer"
      :style="imageContainerStyle"
      @touchstart="startDrag"
      @touchmove="moveImage"
      @touchend="stopDrag"
      @click="closeViewer"
    >
      <img
        ref="image"
        :src="image.url"
        :style="imageStyle"
        @load="centerImage"
      />
    </div>
  </div>
</template>

<script>
import IconButton from "@/components/IconButton.vue";
import scrollLock from "@/helpers/scroll-lock";

export default {
  name: "ImageViewer",
  components: {
    IconButton,
  },
  props: {
    image: {
      type: Object,
      required: true,
    },
  },
  emits: ["close"],
  data() {
    return {
      isDragging: false,
      startX: null,
      startY: null,
      imageX: 0,
      imageY: 0,
    };
  },
  computed: {
    imageContainerStyle() {
      return {
        "background-image": this.image?.blurry
          ? `url(${this.image.blurry})`
          : "none",
      };
    },
    imageStyle() {
      return {
        transform: `translate(${this.imageX}px, ${this.imageY}px)`,
      };
    },
  },
  mounted() {
    const siteHtml = document.getElementsByTagName("html")[0];
    siteHtml.classList.add("slideup-lock-scroll");
    scrollLock.enable();
  },
  methods: {
    startDrag(e) {
      this.isDragging = true;
      this.startX = e.touches[0].clientX - this.imageX;
      this.startY = e.touches[0].clientY - this.imageY;
    },
    moveImage(e) {
      if (!this.isDragging) return;
      const moveX = e.touches[0].clientX - this.startX;
      const moveY = e.touches[0].clientY - this.startY;
      const imageContainer = this.$refs.viewer;
      const maxX = imageContainer.clientWidth - this.$refs.image.clientWidth;
      const maxY = imageContainer.clientHeight - this.$refs.image.clientHeight;
      this.imageX = Math.min(0, Math.max(maxX, moveX));
      this.imageY = Math.min(0, Math.max(maxY, moveY));
    },
    stopDrag() {
      this.isDragging = false;
    },
    centerImage() {
      const imageContainer = this.$refs.viewer;
      const maxX = imageContainer.clientWidth - this.$refs.image.clientWidth;
      this.imageX = maxX / 2;
    },
    closeViewer() {
      this.$emit("close");
      const siteHtml = document.getElementsByTagName("html")[0];
      siteHtml.classList.remove("slideup-lock-scroll");
      scrollLock.disable();
    },
  },
};
</script>

<style lang="scss" scoped>
.image-viewer--wrapper {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 9;

  .close-btn {
    background-color: $color-white;
    color: $color-black;
    border-radius: 0 0 0 8px;
    padding: 10px;
    position: absolute;
    top: 0;
    right: 0;
    z-index: 9;
  }
}

.image-viewer {
  position: relative;
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
  background-size: auto 150%;
  background-repeat: no-repeat;
  background-position: top center;
  cursor: url("data:image/svg+xml;base64,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")
      28 28,
    auto;

  &::after {
    content: "";
    backdrop-filter: blur(5px);
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1;

    @include respond-above(md) {
      backdrop-filter: blur(20px);
    }
  }

  @include respond-above(md) {
    overflow-y: scroll;
    background-size: cover;
  }

  img {
    position: absolute;
    left: 0;
    top: 0;
    min-width: 100%;
    min-height: 250vh;
    z-index: 2;

    @include respond-above(md) {
      width: 100%;
      height: auto;
      min-height: auto;
      min-width: auto;
    }
  }
}
</style>
